import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { familyProfiles } from '../data/familyProfiles.js';
import PageNavigation from '../components/PageNavigation.jsx';

export default function ProfilePage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const profile = familyProfiles.find(p => p.id === profileId);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(profile || {});

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
        <h2>Profile not found</h2>
        <button 
          onClick={() => navigate('/')}
          style={{
            marginTop: 20,
            padding: '12px 24px',
            background: '#c9a62b',
            border: 'none',
            borderRadius: 9999,
            color: '#000',
            cursor: 'pointer'
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  const calculateAge = (dob) => {
    const [day, month, year] = dob.split('.');
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getRoleIcon = (role) => {
    const icons = {
      'FATHER': '👨',
      'MOTHER': '👩',
      'ELDER SON': '👦',
      'YOUNGER SON': '👦',
      '2ND YOUNGER SON': '👦'
    };
    return icons[role] || '👤';
  };

  // Custom photo positioning for specific profiles
  const getPhotoStyle = (profileId) => {
    const customStyles = {
      'jeyakumar-p': {
        width: '140%',
        height: '140%',
        objectPosition: 'center 35%'
      },
      'thamoj-karthic-j': {
        width: '140%',
        height: '140%',
        objectPosition: 'center 35%'
      }
    };
    
    return customStyles[profileId] || {
      width: '120%',
      height: '120%',
      objectPosition: 'center 20%'
    };
  };

  const handleSave = () => {
    // Here you would save to database
    console.log('Saving profile:', editedProfile);
    setIsEditing(false);
    // TODO: Implement actual save to Supabase
  };

  const InfoRow = ({ label, value, field }) => (
    <div style={{
      display: 'flex',
      padding: '16px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      alignItems: 'center'
    }}>
      <div style={{ 
        width: 180, 
        color: '#888', 
        fontSize: 14,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 1
      }}>
        {label}
      </div>
      <div style={{ 
        flex: 1, 
        color: '#fff', 
        fontSize: 16 
      }}>
        {isEditing && field ? (
          <input
            type="text"
            value={editedProfile[field]}
            onChange={(e) => setEditedProfile({...editedProfile, [field]: e.target.value})}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(201,166,43,0.3)',
              borderRadius: 6,
              color: '#fff',
              fontSize: 16
            }}
          />
        ) : (
          value
        )}
      </div>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#000',
      padding: '40px 20px'
    }}>
      {/* Page Navigation */}
      <PageNavigation backTo="/" backLabel="← Back to Home" />
      
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          marginBottom: 32,
          marginTop: 40
        }}>
          {isEditing ? (
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setIsEditing(false)}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,107,107,0.3)',
                  borderRadius: 9999,
                  color: '#ff6b6b',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #c9a62b 0%, #d4af37 100%)',
                  border: 'none',
                  borderRadius: 9999,
                  color: '#000',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Save Changes
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '10px 20px',
                background: 'rgba(201,166,43,0.1)',
                border: '1px solid rgba(201,166,43,0.3)',
                borderRadius: 9999,
                color: '#c9a62b',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ✏️ Edit Profile
            </button>
          )}
        </div>

        {/* Profile Card */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(201,166,43,0.2)',
          borderRadius: 16,
          padding: 40,
          marginBottom: 32
        }}>
          {/* Profile Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 32,
            paddingBottom: 32,
            borderBottom: '2px solid rgba(201,166,43,0.2)',
            marginBottom: 32
          }}>
            {/* Photo */}
            <div style={{
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(201,166,43,0.2) 0%, rgba(201,166,43,0.1) 100%)',
              border: '4px solid rgba(201,166,43,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative'
            }}>
              <img 
                src={profile.photo} 
                alt={profile.name}
                style={{
                  minWidth: '100%',
                  minHeight: '100%',
                  ...getPhotoStyle(profile.id),
                  objectFit: 'cover',
                  position: 'absolute'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<div style="font-size: 72px">${getRoleIcon(profile.familyRole)}</div>`;
                }}
              />
            </div>

            {/* Name and Role */}
            <div style={{ flex: 1 }}>
              <h1 style={{
                color: '#c9a62b',
                fontSize: 36,
                margin: '0 0 8px 0',
                fontFamily: 'Arsenica, Times New Roman, serif'
              }}>
                {profile.name}
              </h1>
              <div style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(201,166,43,0.15)',
                border: '1px solid rgba(201,166,43,0.3)',
                borderRadius: 12,
                color: '#c9a62b',
                fontSize: 13,
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 16
              }}>
                {profile.familyRole}
              </div>
              <div style={{
                display: 'flex',
                gap: 24,
                marginTop: 16,
                fontSize: 14
              }}>
                <div>
                  <div style={{ color: '#888', marginBottom: 4 }}>Age</div>
                  <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                    {calculateAge(profile.dob)} years
                  </div>
                </div>
                <div>
                  <div style={{ color: '#888', marginBottom: 4 }}>Occupation</div>
                  <div style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                    {profile.occupation}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div>
            <h2 style={{
              color: '#c9a62b',
              fontSize: 24,
              marginBottom: 24,
              fontFamily: 'Arsenica, Times New Roman, serif'
            }}>
              Personal Information
            </h2>

            <InfoRow label="Date of Birth" value={profile.dob} field="dob" />
            <InfoRow label="Place of Birth" value={profile.placeOfBirth} field="placeOfBirth" />
            <InfoRow label="Sex" value={profile.sex} field="sex" />
            <InfoRow label="Blood Group" value={profile.bloodGroup} field="bloodGroup" />
            <InfoRow label="Height" value={profile.height} field="height" />
            <InfoRow label="Weight" value={profile.weight} field="weight" />
            
            <h2 style={{
              color: '#c9a62b',
              fontSize: 24,
              marginTop: 40,
              marginBottom: 24,
              fontFamily: 'Arsenica, Times New Roman, serif'
            }}>
              Contact Information
            </h2>

            <InfoRow label="Residence" value={profile.residence} field="residence" />
            <InfoRow label="Contact" value={profile.contact} field="contact" />
            <InfoRow label="Email" value={profile.email} field="email" />
          </div>
        </div>

        {/* Additional Notes Section */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(201,166,43,0.2)',
          borderRadius: 16,
          padding: 32
        }}>
          <h2 style={{
            color: '#c9a62b',
            fontSize: 24,
            marginBottom: 16,
            fontFamily: 'Arsenica, Times New Roman, serif'
          }}>
            Additional Notes
          </h2>
          <p style={{ color: '#888', fontSize: 14, marginBottom: 20 }}>
            Add personal notes, achievements, or special memories about this family member.
          </p>
          <textarea
            placeholder="Write something special..."
            style={{
              width: '100%',
              minHeight: 150,
              padding: 16,
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(201,166,43,0.2)',
              borderRadius: 8,
              color: '#fff',
              fontSize: 15,
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
          />
          <button
            style={{
              marginTop: 16,
              padding: '10px 24px',
              background: 'rgba(201,166,43,0.1)',
              border: '1px solid rgba(201,166,43,0.3)',
              borderRadius: 9999,
              color: '#c9a62b',
              cursor: 'pointer'
            }}
          >
            Save Notes
          </button>
        </div>
      </div>
    </div>
  );
}
